import React, { useEffect, useState } from "react";
import get from "lodash/get";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Spin, message, Input } from "antd";
import { approvedCabinetTable } from "../../constants/cabinet";
import { getApi, patchApi, postApi } from "../../redux/rest/restSlice";
import {
  APPROVED_CABINET,
  APPROVE_CABINET,
  UNAPPROVED_CABINET,
} from "../../constants/actionTypes";
import AddEditApprovedCabinet from "./addEditApprovedCabinet";
import { hasAllPermissions } from "../../redux/selectors/authSelector";
import { PERMISSIONS } from "../../constants/permissions";
import { TABLE_PAGINATION } from "../../utilities/helper";
import DataTable from "../DataTable";
import SummaryStrip from "../SummaryStrip";

const CabinetTable = ({ type, tab }) => {
  const [loading, setLoading] = useState(true);
  const [editModalInfo, setEditModalInfo] = useState({
    visible: false,
    data: null,
  });
  const [cabinetData, setCabinetData] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [unapprovedSearch, setUnapprovedSearch] = useState("");

  const dispatch = useDispatch();

  const toggleEditModal = (data = null) => {
    setEditModalInfo({
      visible: !get(editModalInfo, "visible"),
      data: data,
    });
  };
  const canUpdateCabinet = useSelector(
    hasAllPermissions([PERMISSIONS.UPDATE_CABINET]),
  );

  const canApproveCabinet = useSelector(
    hasAllPermissions([PERMISSIONS.CREATE_CABINET]),
  );

  const fetchApprovedCabinetList = (search = appliedSearch) => {
    setLoading(true);
    const term = (search || "").trim();
    const payload = {
      filters: {
        is_active: type === "active",
        // Hardware ID search
        ...(term ? { cabinet_sn: term } : {}),
      },
    };

    dispatch(
      postApi({
        endpoint: APPROVED_CABINET,
        payload,
      }),
    )
      .then(unwrapResult)
      .then((result) => {
        setLoading(false);
        setCabinetData(get(result, "data"));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchUnapprovedCabinetList = (cabinetSn) => {
    setLoading(true);
    setUnapprovedSearch((cabinetSn || "").trim());

    const queryParams =
      cabinetSn && cabinetSn.trim() ? `?cabinet_sn=${cabinetSn.trim()}` : "";

    dispatch(
      getApi({
        endpoint: `${UNAPPROVED_CABINET}${queryParams}`,
      }),
    )
      .then(unwrapResult)
      .then((result) => {
        setLoading(false);
        setCabinetData(get(result, "data"));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onSucces = (action) => {
    if (action === "register") {
      fetchUnapprovedCabinetList();
    } else {
      fetchApprovedCabinetList();
    }
    toggleEditModal();
  };

  const onApproveCabinet = (id) => {
    setLoading(true);
    dispatch(
      patchApi({
        endpoint: `${APPROVE_CABINET}/${id}`,
      }),
    )
      .then(unwrapResult)
      .then((result) => {
        setLoading(false);
        message.success(get(result, "message"));
        fetchUnapprovedCabinetList();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (tab === "unapproved") {
      fetchUnapprovedCabinetList();
    } else {
      fetchApprovedCabinetList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isUnapproved = tab === "unapproved";

  const onSearch = (value) => {
    if (isUnapproved) {
      fetchUnapprovedCabinetList(value);
    } else {
      setAppliedSearch(value);
      fetchApprovedCabinetList(value);
    }
  };

  const clearSearch = () => {
    setSearchValue("");
    onSearch("");
  };

  const rows = get(cabinetData, "data") || [];
  const firstLoad = loading && !rows.length;
  const online = rows.filter((row) => row.is_working).length;

  let summary = [];
  if (rows.length) {
    summary = isUnapproved
      ? [{ label: "Awaiting approval", value: rows.length, tone: "warning" }]
      : [
          { label: "Total", value: rows.length },
          { label: "Online", value: online, tone: "success" },
          { label: "Offline", value: rows.length - online, tone: "danger" },
        ];
  }

  return (
    <Spin spinning={loading && !firstLoad}>
      <div className="list-toolbar">
        <div className="list-toolbar__main">
          <Input.Search
            className="list-toolbar__search"
            placeholder={isUnapproved ? "Search by Cabinet SN" : "Search by Hardware ID"}
            allowClear
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={onSearch}
          />
        </div>

        {isUnapproved && (
          <div className="list-toolbar__actions">
            <Button
              type="primary"
              className="default-primary-btn"
              onClick={() => toggleEditModal()}
            >
              Add New Device
            </Button>
          </div>
        )}
      </div>

      {!firstLoad && <SummaryStrip items={summary} />}

      <div className="table-container">
        <DataTable
          skeleton={firstLoad}
          scrollX={600}
          rowKey="id"
          className="mt-20"
          dataSource={rows}
          pagination={TABLE_PAGINATION}
          columns={approvedCabinetTable({
            tab,
            updateCabinet: toggleEditModal,
            onApproveCabinet,
            canUpdateCabinet,
            canApproveCabinet,
          })}
          emptyProps={{
            noun: "cabinets",
            search: isUnapproved ? unapprovedSearch : appliedSearch,
            onClearSearch: clearSearch,
          }}
        />
      </div>

      <Modal
        width={640}
        title={get(editModalInfo, "data") ? "Edit Cabinet" : "Add Cabinet"}
        destroyOnClose
        open={get(editModalInfo, "visible")}
        onCancel={toggleEditModal}
        footer={false}
      >
        <AddEditApprovedCabinet
          modalInfo={editModalInfo}
          setModalInfo={toggleEditModal}
          onSucces={onSucces}
          tab={tab}
        />
      </Modal>
    </Spin>
  );
};

export default CabinetTable;
